import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { store } from './store';
import { wagmiConfig, wagmiAdapter, supportedChains, WC_PROJECT_ID } from './config/wagmi';
import { createAppKit } from '@reown/appkit/react';
import { WagmiWeb3Provider } from './contexts/WagmiWeb3Context';
import { UnifiedWalletProvider } from './contexts/UnifiedWalletContext';

import Header from './components/Header';
import Home from './components/Home';
import DeployContract from './components/DeployContract';
import ContractList from './components/ContractList';
import PresaleDashboard from './components/PresaleDashboard';
import PresaleSettings from './components/PresaleSettings';
import AdminTransactions from './components/AdminTransactions';
import NotFound from './components/NotFound';
import NotificationSystem from './components/NotificationSystem';
import ClerkTokenProvider from './components/ClerkTokenProvider';
import Landing from './components/Landing';
import SignInPage from './components/SignInPage';
import SignUpPage from './components/SignUpPage';
import { useUserRole } from './hooks/useUserRole';

import './App.css';

const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'Floobiz';
const SITE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://floobiz.io';

if (WC_PROJECT_ID) {
  createAppKit({
    adapters: [wagmiAdapter],
    networks: supportedChains,
    projectId: WC_PROJECT_ID,
    metadata: {
      name: SITE_NAME,
      description: `${SITE_NAME} token sale`,
      url: SITE_ORIGIN,
      icons: [`${SITE_ORIGIN}/logo192.png`],
    },
    features: {
      analytics: false,
      email: false,
      socials: false,
      onramp: false,
      swaps: false,
    },
  });
} else if (typeof window !== 'undefined') {
  console.warn(
    `[${SITE_NAME}] REACT_APP_WALLETCONNECT_PROJECT_ID is not set — AppKit modal disabled. ` +
    'Get a free project ID at https://cloud.reown.com to enable WalletConnect.'
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status === 429 || (status >= 400 && status < 500)) return false;
        return failureCount < 2;
      },
    },
  },
});

function RedirectToSignIn({ to }) {
  const location = useLocation();
  const target = to || `${location.pathname}${location.search}`;
  const url = `/sign-in?redirect_url=${encodeURIComponent(target)}`;
  return <Navigate to={url} replace />;
}

function AdminGate({ children }) {
  const { isAdmin, isLoading } = useUserRole();
  if (isLoading) return null;
  // Send non-admins to the landing page rather than /buy — admin routes should
  // be invisible to regular buyers, not bounce them into the sale UI.
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <Provider store={store}>
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <WagmiWeb3Provider>
            <UnifiedWalletProvider>
              <Router>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/presale" element={<Navigate to="/buy" replace />} />
                  <Route path="/presale-v2" element={<Navigate to="/buy" replace />} />

                  <Route path="/sign-in/*" element={<SignInPage />} />
                  <Route path="/sign-up/*" element={<SignUpPage />} />

                  {/* Buy page — Clerk-gated (any signed-in user). Wallet is still the
                      on-chain identity; Clerk just guards access and lets us log
                      (clerkId, wallet, txHash) for audit. */}
                  <Route
                    path="/buy"
                    element={
                      <>
                        <SignedOut>
                          <RedirectToSignIn to="/buy" />
                        </SignedOut>
                        <SignedIn>
                          <ClerkTokenProvider>
                            <PresaleDashboard />
                          </ClerkTokenProvider>
                        </SignedIn>
                      </>
                    }
                  />

                  {/* Admin — Clerk gated */}
                  <Route
                    path="*"
                    element={
                      <>
                        <SignedOut>
                          <RedirectToSignIn />
                        </SignedOut>

                        <SignedIn>
                          <ClerkTokenProvider>
                            <AdminGate>
                              <div className="App">
                                <Header />
                                <main className="main-content">
                                  <Routes>
                                    <Route path="/admin" element={<Home />} />
                                    <Route path="/deploy" element={<DeployContract />} />
                                    <Route path="/contracts" element={<ContractList />} />
                                    <Route path="/admin/presale" element={<PresaleSettings />} />
                                    <Route path="/admin/transactions" element={<AdminTransactions />} />
                                    <Route path="*" element={<NotFound />} />
                                  </Routes>
                                </main>
                                <NotificationSystem />
                              </div>
                            </AdminGate>
                          </ClerkTokenProvider>
                        </SignedIn>
                      </>
                    }
                  />
                </Routes>
              </Router>
            </UnifiedWalletProvider>
          </WagmiWeb3Provider>
        </QueryClientProvider>
      </WagmiProvider>
    </Provider>
  );
}

export default App;
